/**
 * Insurance meta and checkout builders
 *
 * Computes reimbursement amounts, explain/checkout copy, and checkout table config from
 * catalog insurers or persisted patient `webAppOnboarding` billing fields.
 *
 * References:
 * - `insurance/config.ts`
 * - `insurance/routing.ts`
 */

import type {
  BnplCohortConfig,
  Insurance,
  SignedUpPatient,
  WebAppOnboardingState,
} from '@physiofit-digital/physiofit_types';
import type {
  CheckoutTableConfig,
  InsuranceBillingType,
  InsuranceMeta,
} from './config';
import { isBlockedTempExplainReason, readDisplayableBlockedReason } from './config';
import {
  buildAbtretungExplainMeta,
  buildWithoutAbtretungPatientExplainMeta,
  buildWithoutAbtretungPhysiofitExplainMeta,
  DEFAULT_BLOCKED_EXPLAIN,
} from './copy';
import { formatRefundPercent } from './copyUtils';
import { readBillingPatientBlockedReason, readBillingPatientType } from './normalize';

/**
 * Platform default course fee in EUR, used when an insurer carries no `coursePrice` (ERS-26).
 *
 * Must stay equal to `appInfo/physiofitBillableData.coursePrice` — that document is what the GKV
 * submission documents (Abtretungserklärung, Teilnahmebescheinigung, Zahlungsbestätigung) are filled
 * from — and to the same default in the BNPL cloud functions, which is what the provider actually
 * charges. A patient must never be shown a number that differs from either. It is duplicated here
 * rather than read because that document is not client-readable.
 */
export const ONBOARDING_COURSE_FEE_FALLBACK_EUR = 150;

export type WebAppOnboardingInsuranceInfo = NonNullable<WebAppOnboardingState['insuranceInfo']>;
export type WebAppOnboardingDisplayedBillingInfo = NonNullable<
  WebAppOnboardingState['displayedBillingInfo']
>;

/**
 * Gross course fee in EUR for this insurer.
 *
 * @param {Object | null | undefined} insurance Selected catalog insurer, when one is resolved.
 * @return {number} The insurer's configured fee, or the platform default.
 */
export function resolveCourseFeeEur(insurance?: Insurance | null): number {
  const configured = insurance?.coursePrice;
  if (configured != null && Number.isFinite(configured) && configured > 0) {
    return configured;
  }
  return ONBOARDING_COURSE_FEE_FALLBACK_EUR;
}

export function resolveReimbursementEur(insurance: Insurance, courseFee: number): number {
  const rate = insurance.refundRate;
  if (rate == null || Number.isNaN(rate)) {
    return 0;
  }
  let calculated = Math.round(courseFee * rate);
  if (insurance.refundMaxAmount != null && !Number.isNaN(insurance.refundMaxAmount)) {
    calculated = Math.min(calculated, insurance.refundMaxAmount);
  }
  return calculated;
}

/**
 * What the selected insurer actually covers of the course fee (ERS-61 / ERS-26).
 *
 * The design's "Full-Refund" vs "Partial-Refund" axis is about the AMOUNT, not about `refundRate`:
 * `refundMaxAmount` can undercut a rate of 1 (BARMER refunds 100 € of a 150 € course), and the same
 * cap is applied server-side when the GKV documents are filled. Branching copy on `refundRate < 1`
 * therefore produced text that contradicted the amount printed next to it for 16 of the 50 bonus
 * insurers in production. Every branch on this axis must read `isFullCoverage`.
 */
export type InsuranceCoverage = {
  /** Gross course fee in EUR — per-insurer `coursePrice`, or the platform fallback. */
  courseFeeEur: number;
  /** What the insurer reimburses to the patient: `rate × fee`, capped by `refundMaxAmount`. */
  insurerRefundEur: number;
  /** What Physiofit tops up so the patient's own share is zero. `0` on full coverage. */
  physiofitSubsidyEur: number;
  /** True when the insurer alone covers the whole fee — the design's `Full-Refund` frames. */
  isFullCoverage: boolean;
  /**
   * Insurer share of the fee, whole-number percent, derived from the AMOUNT. Not
   * `formatRefundPercent(refundRate)`: where the cap bites, the rate overstates the share
   * (DAK refunds 75 € of 150 € at `refundRate` 0.8 — 50 %, not 80 %).
   */
  insurerSharePercent: number;
};

/**
 * Builds the coverage facts for a catalog insurer.
 *
 * @param {Object} insurance Selected catalog insurer.
 * @return {Object | null} Coverage facts, or null when the insurer carries no usable `refundRate`.
 */
export function resolveInsuranceCoverage(insurance: Insurance): InsuranceCoverage | null {
  const rate = insurance.refundRate;
  if (rate == null || Number.isNaN(rate)) {
    return null;
  }
  const courseFeeEur = resolveCourseFeeEur(insurance);
  const insurerRefundEur = resolveReimbursementEur(insurance, courseFeeEur);
  return buildCoverage(courseFeeEur, insurerRefundEur);
}

/**
 * Same facts, rebuilt from the persisted `displayedBillingInfo` snapshot, so a patient who returns
 * after a catalogue change reads the variant they were originally quoted (C3). Deliberately ignores
 * `insurerRefundRate`: the snapshot's amounts are the binding record.
 *
 * @param {Object} billing Persisted `webAppOnboarding.displayedBillingInfo`.
 * @return {Object | null} Coverage facts, or null when the snapshot carries no amounts.
 */
export function resolveCoverageFromDisplayedBilling(
  billing: WebAppOnboardingDisplayedBillingInfo,
): InsuranceCoverage | null {
  const courseFeeEur = billing.invoiceTotalEur;
  const insurerRefundEur = billing.insurerRefundAmountEur;
  if (courseFeeEur == null || insurerRefundEur == null) {
    return null;
  }
  return buildCoverage(courseFeeEur, insurerRefundEur);
}

function buildCoverage(courseFeeEur: number, insurerRefundEur: number): InsuranceCoverage {
  const physiofitSubsidyEur = Math.max(0, courseFeeEur - insurerRefundEur);
  return {
    courseFeeEur,
    insurerRefundEur,
    physiofitSubsidyEur,
    isFullCoverage: physiofitSubsidyEur === 0,
    insurerSharePercent: courseFeeEur > 0 ? Math.round((100 * insurerRefundEur) / courseFeeEur) : 0,
  };
}

export function resolveBillingTypeFromInsurance(insurance: Insurance): InsuranceBillingType {
  const patientType = readBillingPatientType(insurance);
  if (
    patientType === 'blocked' ||
    patientType === 'invoice' ||
    patientType === 'abtretung' ||
    patientType === 'without_abtretung_physiofit' ||
    patientType === 'without_abtretung_patient' ||
    patientType === 'bnpl'
  ) {
    return patientType;
  }
  if (insurance.privateInsurance) {
    return 'blocked';
  }
  return 'abtretung';
}

/**
 * Insurers that require the patient to hand in the course themselves, through the insurer's own app,
 * instead of Physiofit submitting it. Today that is the TK. The catalog has no field for it yet, and
 * `submitType: 'no_submit'` is not the signal (KKH carries it for a different reason).
 *
 * @param {Object} insurance Selected catalog insurer.
 * @return {boolean} True when the BNPL copy must describe the patient, not Physiofit, as the submitter.
 */
export function isPatientSubmittingInsurer(insurance: Insurance): boolean {
  return insurance.slug === 'tk';
}

/**
 * Effective billing flow for a patient's selected insurer.
 *
 * Phase 2 (broad launch): the catalog decides — an insurer whose primary type is `bnpl` routes there.
 * Phase 1 (beta, ERS-5): the catalog still says `invoice`/`abtretung`, so the cohort decides. BOTH
 * halves must hold — the inviting practice and the selected insurer are each in the cohort — and the
 * condition is evaluated here, at selection time, never precomputed.
 *
 * The cohort can only override an insurer the provider can be repaid from. A `blocked` one (private,
 * temporarily blocked, refund below 100) or one without a refund rate has no BNPL cost table to show,
 * so it keeps its catalog flow even in an all-insurers cohort.
 *
 * @param {Object} insurance Selected catalog insurer.
 * @param {Object | undefined} patient Signed-up patient document (its `practiceId` is the inviting practice).
 * @param {Object | null} cohort Beta cohort config, or null when unavailable.
 * @return {string} Effective billing flow for routing and persistence.
 */
export function resolveEffectiveBillingType(
  insurance: Insurance,
  patient: SignedUpPatient | undefined,
  cohort: BnplCohortConfig | null,
): InsuranceBillingType {
  const catalogType = resolveBillingTypeFromInsurance(insurance);
  if (catalogType === 'bnpl') {
    return 'bnpl';
  }
  if (catalogType === 'blocked' || !resolveInsuranceCoverage(insurance)) {
    return catalogType;
  }
  if (isInBnplBetaCohort(insurance, patient, cohort)) {
    return 'bnpl';
  }
  return catalogType;
}

/**
 * Whether this patient/insurer pair is in the BNPL beta cohort.
 *
 * @param {Object} insurance Selected catalog insurer.
 * @param {Object | undefined} patient Signed-up patient document.
 * @param {Object | null} cohort Beta cohort config.
 * @return {boolean} True only when practice AND insurer are both enabled and no decline is recorded.
 */
export function isInBnplBetaCohort(
  insurance: Insurance,
  patient: SignedUpPatient | undefined,
  cohort: BnplCohortConfig | null,
): boolean {
  if (!cohort || !patient?.practiceId) {
    return false;
  }
  // A declined patient must not be funnelled back in when they return to insurance-select.
  if (patient.webAppOnboarding?.flags?.bnplDeclinedDate) {
    return false;
  }
  const entry = cohort.practices?.[patient.practiceId];
  if (!entry) {
    return false;
  }
  if (entry.allInsurances) {
    return true;
  }
  // Absent or empty list means NOBODY, never everybody — see the note in useBnplCohort.ts.
  return Boolean(entry.insuranceIds?.includes(insurance.id));
}

function buildPartialRefundDisclaimer(
  courseFee: number,
  subsidy: number,
  reimbursement: number,
  insuranceName: string,
): string {
  return `Nach Kursabschluss erhältst von uns eine Rechnung über ${courseFee}€. Sobald die Zahlung bei uns eingegangen ist, reichen wir deine Unterlagen bei der ${insuranceName} ein und zahlen dir die Förderung in Höhe von ${subsidy}€ auf dein Bankkonto aus. Die ${insuranceName} erstattet dir die verbleibenden ${reimbursement}€ innerhalb der nächsten Werktage.`;
}

function buildFullRefundDisclaimer(courseFee: number, insuranceName: string): string {
  return `Bei Kursabschluss bekommst du von uns eine Rechnung über ${courseFee} €. Diese ${courseFee} € erstattet dir die ${insuranceName} komplett — du leitest die Erstattung einfach weiter.`;
}

export function buildCheckoutTableConfig(
  insurance: Insurance,
  displayName: string,
): CheckoutTableConfig | null {
  if (resolveBillingTypeFromInsurance(insurance) !== 'invoice') {
    return null;
  }

  const refundRate = insurance.refundRate;
  if (refundRate == null || Number.isNaN(refundRate)) {
    return null;
  }

  const courseFee = resolveCourseFeeEur(insurance);
  const reimbursement = resolveReimbursementEur(insurance, courseFee);
  const subsidy = courseFee - reimbursement;
  const bonus = insurance.bonus;
  const bonusEur = 0;
  const showFooter = false;

  return {
    courseFeeEur: courseFee,
    courseFeeLabel: 'Kursgebühr',
    reimbursementEur: reimbursement,
    // F2: the true insurer share can undercut `refundRate` where `refundMaxAmount` caps the amount
    // (DAK refunds 75 € of 150 € at rate 0.8 — 50 %, not 80 %), so the percent is derived from the
    // amount, never from the rate.
    reimbursementLabel: `Erstattung durch die ${displayName} (${formatRefundPercent(reimbursement / courseFee)})`,
    subsidyEur: subsidy,
    subsidyLabel: subsidy > 0 ? 'Förderung Physiofit' : '',
    bonusEur,
    bonusLabel: bonus?.hasBonus ? `Bonuszahlung der ${displayName}` : '',
    yourCostsEur: 0,
    yourCostsLabel: 'Deine Kosten',
    footerLabel: 'Dein Plus',
    a11ySummary: 'Kostenübersicht',
    showPhysiofitSubsidy: subsidy > 0,
    showFooter,
  };
}

export function buildInvoiceExplainMeta(
  insurance: Insurance,
  displayName: string,
): InsuranceMeta['invoiceExplain'] | undefined {
  const bonus = insurance.bonus;
  if (!bonus?.hasBonus) {
    return undefined;
  }

  const defaultBody = `Nach Kursabschluss erhältst du mit unserer Teilnahmebestätigung eine Prämie im Rahmen des Bonusprogramms der ${displayName}.`;

  return {
    bonusTitle: `Prämie von der ${displayName}`,
    bonusBody: bonus.description?.trim() || defaultBody,
  };
}

export function buildInvoiceCheckoutMeta(
  insurance: Insurance,
  displayName: string,
): InsuranceMeta['invoiceCheckout'] | undefined {
  const table = buildCheckoutTableConfig(insurance, displayName);
  if (!table) {
    return undefined;
  }

  const disclaimer =
    table.subsidyEur > 0
      ? buildPartialRefundDisclaimer(
          table.courseFeeEur,
          table.subsidyEur,
          table.reimbursementEur,
          displayName,
        )
      : buildFullRefundDisclaimer(table.courseFeeEur, displayName);

  return {
    headline: 'Die Kursgebühr wird vollständig übernommen.',
    disclaimer,
    detailLinkLabel: 'So läuft die Abrechnung im Detail ->',
  };
}

export function buildBlockedExplainMeta(insurance: Insurance): InsuranceMeta['blockedExplain'] {
  const reason = readBillingPatientBlockedReason(insurance);
  return {
    title: DEFAULT_BLOCKED_EXPLAIN.title,
    body: reason ?? DEFAULT_BLOCKED_EXPLAIN.body,
  };
}

export function buildInsuranceMetaFromInsurance(
  insurance: Insurance,
  displayName: string,
): InsuranceMeta {
  const billingType = resolveBillingTypeFromInsurance(insurance);
  const meta: InsuranceMeta = {};

  if (billingType === 'invoice') {
    const invoiceExplain = buildInvoiceExplainMeta(insurance, displayName);
    const invoiceCheckout = buildInvoiceCheckoutMeta(insurance, displayName);
    const table = buildCheckoutTableConfig(insurance, displayName);

    if (invoiceExplain) {
      meta.invoiceExplain = invoiceExplain;
    }
    if (invoiceCheckout) {
      meta.invoiceCheckout = invoiceCheckout;
    }
    if (table) {
      meta.checkout = { table };
    }
  }

  if (billingType === 'blocked') {
    const blockedReason = readBillingPatientBlockedReason(insurance);
    if (isBlockedTempExplainReason(blockedReason)) {
      meta.abtretungExplain = buildAbtretungExplainMeta(displayName);
    } else {
      meta.blockedExplain = buildBlockedExplainMeta(insurance);
    }
  }

  if (billingType === 'abtretung') {
    meta.abtretungExplain = buildAbtretungExplainMeta(displayName);
  }

  if (billingType === 'without_abtretung_physiofit') {
    meta.withoutAbtretungPhysiofitExplain = buildWithoutAbtretungPhysiofitExplainMeta(displayName);
  }

  if (billingType === 'without_abtretung_patient') {
    meta.withoutAbtretungPatientExplain = buildWithoutAbtretungPatientExplainMeta(displayName);
  }

  return meta;
}

/**
 * Builds the persisted `webAppOnboarding.insuranceInfo` record.
 *
 * @param {Object} insurance Selected catalog insurer.
 * @param {string} displayName Resolved display name for the insurer.
 * @param {string} billingType Effective billing flow, resolved by the caller via
 *   `resolveEffectiveBillingType` — the cohort decision is made once, at insurance-select, and both the
 *   routing and this persisted record use that same value. No fallback field is stored: the insurer's
 *   own type stays derivable from `insurance.id` + `resolveBillingTypeFromInsurance`.
 * @param {Object | undefined} options `practiceChargesFee` from the inviting practice's setting; stamped
 *   only for a private insurer, so it can never be set for a GKV patient.
 * @return {Object} `webAppOnboarding.insuranceInfo` value.
 */
export function buildWebAppOnboardingInsuranceInfo(
  insurance: Insurance,
  displayName: string,
  billingType: InsuranceBillingType,
  options?: { practiceChargesFee?: boolean },
): WebAppOnboardingInsuranceInfo {
  const blockedReason =
    billingType === 'blocked' ? readBillingPatientBlockedReason(insurance) : undefined;
  const practiceChargesFee = Boolean(options?.practiceChargesFee) && insurance.privateInsurance;

  return {
    id: insurance.id,
    name: displayName,
    billingType,
    isPrivateInsurance: insurance.privateInsurance,
    ...(blockedReason ? { blockedReason } : {}),
    ...(practiceChargesFee ? { practiceChargesFee: true } : {}),
  };
}

export function canPersistDisplayedBillingInfo(insurance: Insurance): boolean {
  const rate = insurance.refundRate;
  return rate != null && !Number.isNaN(rate);
}

export function buildDisplayedBillingInfo(
  insurance: Insurance,
): WebAppOnboardingDisplayedBillingInfo {
  const invoiceTotalEur = resolveCourseFeeEur(insurance);
  const insurerRefundAmountEur = resolveReimbursementEur(insurance, invoiceTotalEur);
  const physiofitSubsidyAmountEur = invoiceTotalEur - insurerRefundAmountEur;
  const bonus = insurance.bonus;
  const refundRate = insurance.refundRate;

  return {
    invoiceTotalEur,
    insurerRefundAmountEur,
    physiofitSubsidyAmountEur,
    insurerHasBonusProgram: Boolean(bonus?.hasBonus),
    ...(refundRate != null && !Number.isNaN(refundRate) ? { insurerRefundRate: refundRate } : {}),
  };
}

export function readDisplayedBillingFromWebAppOnboarding(
  webAppOnboarding: WebAppOnboardingState | undefined,
): WebAppOnboardingDisplayedBillingInfo | null {
  const displayed = webAppOnboarding?.displayedBillingInfo;
  if (displayed && displayedBillingHasFinancials(displayed)) {
    return displayed;
  }
  return null;
}

export type BuildInsuranceMetaFromPatientInput = {
  billingType: WebAppOnboardingInsuranceInfo['billingType'];
  insuranceName: string;
  blockedReason?: string;
  displayedBilling: WebAppOnboardingDisplayedBillingInfo | null;
};

export function buildInsuranceMetaFromPatient(
  input: BuildInsuranceMetaFromPatientInput,
): InsuranceMeta {
  const { billingType, insuranceName, blockedReason, displayedBilling } = input;

  if (billingType === 'blocked') {
    if (isBlockedTempExplainReason(blockedReason)) {
      return { abtretungExplain: buildAbtretungExplainMeta(insuranceName) };
    }
    return {
      blockedExplain: {
        title: DEFAULT_BLOCKED_EXPLAIN.title,
        body: readDisplayableBlockedReason(blockedReason) ?? DEFAULT_BLOCKED_EXPLAIN.body,
      },
    };
  }

  if (billingType === 'abtretung') {
    return { abtretungExplain: buildAbtretungExplainMeta(insuranceName) };
  }

  if (billingType === 'without_abtretung_physiofit') {
    return { withoutAbtretungPhysiofitExplain: buildWithoutAbtretungPhysiofitExplainMeta(insuranceName) };
  }

  if (billingType === 'without_abtretung_patient') {
    return { withoutAbtretungPatientExplain: buildWithoutAbtretungPatientExplainMeta(insuranceName) };
  }

  if (billingType !== 'invoice') {
    return {};
  }

  if (!displayedBilling) {
    return {};
  }

  return buildInsuranceMetaFromDisplayedBilling(displayedBilling, insuranceName);
}

export function buildInsuranceMetaFromDisplayedBilling(
  billing: WebAppOnboardingDisplayedBillingInfo,
  insuranceName: string,
): InsuranceMeta {
  // Re-renders from the persisted snapshot, so the price comes from the snapshot too — never from
  // the catalog. A patient returning after a price change must see what they were originally quoted.
  const courseFee = billing.invoiceTotalEur ?? ONBOARDING_COURSE_FEE_FALLBACK_EUR;
  const refundRate = billing.insurerRefundRate ?? 0;
  const reimbursement =
    billing.insurerRefundAmountEur ??
    (refundRate > 0 ? Math.round(courseFee * refundRate) : 0);
  const subsidy = billing.physiofitSubsidyAmountEur ?? courseFee - reimbursement;
  const displayName = insuranceName;
  const bonusEur = 0;

  const table: CheckoutTableConfig = {
    courseFeeEur: courseFee,
    courseFeeLabel: 'Kursgebühr',
    reimbursementEur: reimbursement,
    // F2 (same fix as `buildCheckoutTableConfig`): derive the percent from the amount, not the rate.
    reimbursementLabel: `Erstattung durch die ${displayName} (${formatRefundPercent(courseFee > 0 ? reimbursement / courseFee : 0)})`,
    subsidyEur: subsidy,
    subsidyLabel: subsidy > 0 ? 'Förderung Physiofit' : '',
    bonusEur,
    bonusLabel: billing.insurerHasBonusProgram ? `Bonuszahlung der ${displayName}` : '',
    yourCostsEur: 0,
    yourCostsLabel: 'Deine Kosten',
    footerLabel: 'Dein Plus',
    a11ySummary: 'Kostenübersicht',
    showPhysiofitSubsidy: subsidy > 0,
    showFooter: false,
  };

  const meta: InsuranceMeta = {
    invoiceCheckout: {
      headline: 'Die Kursgebühr wird vollständig übernommen.',
      disclaimer:
        subsidy > 0
          ? buildPartialRefundDisclaimer(courseFee, subsidy, reimbursement, displayName)
          : buildFullRefundDisclaimer(courseFee, displayName),
      detailLinkLabel: 'So läuft die Abrechnung im Detail ->',
    },
    checkout: { table },
  };

  if (billing.insurerHasBonusProgram) {
    meta.invoiceExplain = {
      bonusTitle: `Prämie von der ${displayName}`,
      bonusBody:
        'Nach Kursabschluss erhältst du mit unserer Teilnahmebestätigung eine Prämie im Rahmen des Bonusprogramms deiner Krankenkasse.',
    };
  }

  return meta;
}

export function displayedBillingHasFinancials(
  billing: WebAppOnboardingDisplayedBillingInfo | undefined,
): billing is WebAppOnboardingDisplayedBillingInfo & {
  insurerRefundAmountEur: number;
} {
  if (!billing) {
    return false;
  }
  const reimbursement = billing.insurerRefundAmountEur;
  if (reimbursement != null && !Number.isNaN(reimbursement)) {
    return true;
  }
  const rate = billing.insurerRefundRate;
  return rate != null && !Number.isNaN(rate) && rate > 0;
}
